/* eslint-disable camelcase */
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createSelector, createStructuredSelector } from 'reselect';
import { superChatSelector } from 'ai/selectors/superChatSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import { pageSelector } from 'selectors/page/pageSelector';
import { featuresAIBeautyChatSelector } from 'ai/selectors/testTarget/featuresAIBeautyChatSelector';
import SuperChatActions from 'ai/actions/superChatActions';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { getNewEntryPoint, getContextAwarePlacement, getSearchKeyword } from 'ai/utils/aiBeautyChat';
import { ENTRYPOINTS } from 'ai/constants/superchat';
import { getGENAIAnonymousId, getGENAISession } from 'ai/utils/sessionStorage';
import Empty from 'constants/empty';
import sdnApi from 'ai/services/sdn';

const { entryPointPrompts } = sdnApi;
const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocaleResourceFile } = LanguageLocaleUtils;
const getText = getLocaleResourceFile('ai/components/SuperChat/locales', 'SuperChat');

const localization = createStructuredSelector({
    title: getTextFromResource(getText, 'title'),
    new: getTextFromResource(getText, 'new'),
    startChat: getTextFromResource(getText, 'startChat'),
    tryAsking: getTextFromResource(getText, 'tryAsking'),
    topHairCareQuestions: getTextFromResource(getText, 'topHairCareQuestions'),
    topSkincareQuestions: getTextFromResource(getText, 'topSkincareQuestions'),
    topMakeupQuestions: getTextFromResource(getText, 'topMakeupQuestions'),
    topFragranceQuestions: getTextFromResource(getText, 'topFragranceQuestions'),
    topBathBodyQuestions: getTextFromResource(getText, 'topBathBodyQuestions'),
    description: getTextFromResource(getText, 'description')
});

const functions = (dispatch, _ownProps) => ({
    handlePromptClick: (prompts, page, featuresAIBeautyChat, selectedPrompt) => {
        const entryPointData = getNewEntryPoint(selectedPrompt, page, featuresAIBeautyChat);

        if (entryPointData) {
            // Preserve source: 'header' when getNewEntryPoint returns a context-aware entry point
            entryPointData.source = 'header';
            dispatch(SuperChatActions.showSuperChat(true, prompts, entryPointData));
        } else {
            dispatch(
                SuperChatActions.showSuperChat(true, prompts, {
                    entrypoint: ENTRYPOINTS.DEFAULT,
                    selectedPrompt,
                    source: 'header'
                })
            );
        }
    },
    clearContextPrompts: () => {
        dispatch(SuperChatActions.setContextPrompts(Empty.Array));
    },
    fetchEntryPoints: async (clientId, anonymousId, pageState) => {
        const placement = getContextAwarePlacement(pageState);
        let params;

        if (placement === 'SRP') {
            params = {
                page: ENTRYPOINTS.SRP,
                searchTerm: getSearchKeyword(pageState),
                clientId,
                anonymousId
            };
        } else {
            params = {
                page: ENTRYPOINTS.DEFAULT,
                clientId,
                anonymousId
            };
        }

        const res = await entryPointPrompts(params).then(data => data);
        dispatch(SuperChatActions.setContextPrompts(res?.entrypoint_prompts || Empty.Array));
    }
});

const fields = createSelector(
    superChatSelector,
    coreUserDataSelector,
    pageSelector,
    featuresAIBeautyChatSelector,
    localization,
    (superChat, user, page, featuresAIBeautyChat, locales) => {
        const clientId = user?.biId;
        let anonymousId = undefined;
        let session = undefined;

        if (!Sephora.isNodeRender) {
            anonymousId = getGENAIAnonymousId();
            session = getGENAISession();
        }

        return {
            localization: locales,
            config: superChat.config,
            page,
            featuresAIBeautyChat,
            contextPrompts: superChat.contextPrompts || [],
            clientId,
            anonymousId,
            session
        };
    }
);

const withAIBeautyChatDropProps = compose(wrapHOC(connect(fields, functions)));

export {
    withAIBeautyChatDropProps, fields, functions
};
